import {useState, useCallback, useMemo, type KeyboardEvent} from 'react'
import {CompletionEditor, type Suggestion, getTokenBeforeCursor} from '@/components/ui/completion-editor'
import {matchFkJoinOn, matchFkJoinTable} from './sql-completion'
import {matchCheckConstraint, buildCheckIndex} from './check-completion'
import type {SqlSchema} from './types'

/* eslint-disable i18next/no-literal-string -- SQL keywords, testids, and context detection are language constructs */
const SQL_KEYWORDS_BY_CONTEXT: Record<string, string[]> = {
    start: ['SELECT', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'ALTER', 'DROP', 'WITH'],
    afterSelect: ['DISTINCT', '*', 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'CASE'],
    afterFrom: [],
    afterWhere: ['EXISTS', 'NOT', 'IN'],
    afterOrder: ['BY'],
    afterGroup: ['BY'],
    joinModifiers: ['LEFT', 'RIGHT', 'INNER', 'OUTER', 'CROSS'],
    general: [
        'SELECT', 'FROM', 'WHERE', 'JOIN', 'LEFT', 'RIGHT', 'INNER', 'OUTER', 'CROSS',
        'ON', 'AND', 'OR', 'NOT', 'IN', 'IS', 'NULL', 'LIKE', 'BETWEEN', 'EXISTS',
        'ORDER', 'GROUP', 'HAVING', 'LIMIT', 'OFFSET', 'UNION', 'AS', 'DISTINCT',
        'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE', 'CREATE', 'ALTER', 'DROP',
        'TABLE', 'WITH', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'ASC', 'DESC',
        'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'BY',
    ],
}
interface SqlEditorProps {
    sql: string
    onChange: (sql: string) => void
    onRun?: () => void
    schema?: SqlSchema
    readOnly?: boolean
    minHeight?: string
    maxHeight?: string
    keymaps?: {key: string; mac: string; run: () => boolean}[]
}

function getContextKeywords(textBefore: string): string[] {
    const trimmed = textBefore.trimEnd()
    const upperTrimmed = trimmed.toUpperCase()

    if (!trimmed || /;\s*$/.test(trimmed)) {
        return SQL_KEYWORDS_BY_CONTEXT.start
    }

    if (/\bORDER\s*$/i.test(trimmed) || /\bGROUP\s*$/i.test(trimmed)) {
        return ['BY']
    }

    if (/\bSELECT\s+$/i.test(trimmed) || /,\s*$/i.test(trimmed) && upperTrimmed.includes('SELECT') && !upperTrimmed.includes('FROM')) {
        return SQL_KEYWORDS_BY_CONTEXT.afterSelect
    }

    if (/\b(?:FROM|JOIN)\s+$/i.test(trimmed)) {
        return SQL_KEYWORDS_BY_CONTEXT.afterFrom
    }

    if (/\b(?:WHERE|AND|OR|ON)\s+$/i.test(trimmed)) {
        return SQL_KEYWORDS_BY_CONTEXT.afterWhere
    }

    // After table name and space — suggest JOIN, WHERE, ORDER, etc.
    if (/\bFROM\s+\w+\s+$/i.test(trimmed) || /\bJOIN\s+\w+\s+ON\s+\S+\s+$/i.test(trimmed)) {
        return [...SQL_KEYWORDS_BY_CONTEXT.joinModifiers, 'JOIN', 'WHERE', 'ORDER', 'GROUP', 'HAVING', 'LIMIT']
    }

    return SQL_KEYWORDS_BY_CONTEXT.general
}

function getTableNames(schema?: SqlSchema): string[] {
    return schema?.tables?.map(t => t.name) ?? []
}

function getColumnNames(schema: SqlSchema | undefined, tableName: string): string[] {
    if (!schema?.tables) return []
    const table = schema.tables.find(t => t.name.toLowerCase() === tableName.toLowerCase())
    return table?.columns.map(c => c.name) ?? []
}

export function SqlEditor({sql, onChange, onRun, schema, readOnly, minHeight = '120px', maxHeight = '400px'}: SqlEditorProps) {
    const [suggestions, setSuggestions] = useState<Suggestion[]>([])

    const checkIndex = useMemo(() => {
        if (!schema?.checkConstraints) return null
        return buildCheckIndex(schema.checkConstraints)
    }, [schema])

    const handleSuggestionsRequested = useCallback((text: string, cursorPosition: number) => {
        const textBefore = text.substring(0, cursorPosition)
        const token = getTokenBeforeCursor(text, cursorPosition).toLowerCase()
        const results: Suggestion[] = []

        // 1. FK JOIN ON conditions (highest priority)
        if (schema?.foreignKeys) {
            const fkOnSuggestions = matchFkJoinOn(textBefore, schema.foreignKeys)
            if (fkOnSuggestions.length > 0) {
                results.push(...fkOnSuggestions)
                setSuggestions(withRange(filterByToken(results, token), cursorPosition, token.length))
                return
            }
        }

        // 2. FK JOIN table suggestions
        if (schema?.foreignKeys) {
            const fkTableSuggestions = matchFkJoinTable(textBefore, schema.foreignKeys)
            if (fkTableSuggestions.length > 0) {
                results.push(...fkTableSuggestions)
            }
        }

        // 3. CHECK constraint enum values
        if (checkIndex) {
            const checkSuggestions = matchCheckConstraint(textBefore, checkIndex)
            if (checkSuggestions.length > 0) {
                setSuggestions(withRange(checkSuggestions, cursorPosition, token.length))
                return
            }
        }

        // 4. Column names after table. (dot notation)
        const dotMatch = textBefore.match(/(\w+)\.(\w*)$/i)
        if (dotMatch) {
            const tableName = dotMatch[1]
            const columnPartial = dotMatch[2].toLowerCase()
            const columns = getColumnNames(schema, tableName)
            if (columns.length > 0) {
                const columnSuggestions = columns
                    .filter(c => c.toLowerCase().startsWith(columnPartial))
                    .map(c => ({value: c, description: 'column'}))
                setSuggestions(withRange(columnSuggestions, cursorPosition, columnPartial.length))
                return
            }
        }

        // 5. Table names in FROM/JOIN context
        const tableNames = getTableNames(schema)
        if (/\b(?:FROM|JOIN)\s+\w*$/i.test(textBefore)) {
            const tableSuggestions = tableNames
                .filter(t => t.toLowerCase().startsWith(token))
                .map(t => ({value: t, description: 'table'}))
            results.push(...tableSuggestions)
        }

        // 6. Table names in SELECT, WHERE, ORDER BY context (for qualified column refs)
        if (/\b(?:SELECT|WHERE|AND|OR|ON|ORDER\s+BY|GROUP\s+BY)\s+(?:.*,\s*)?\w*$/i.test(textBefore) && !dotMatch) {
            const tableSuggestions = tableNames
                .filter(t => t.toLowerCase().startsWith(token) && !results.some(r => r.value === t))
                .map(t => ({value: t, description: 'table'}))
            results.push(...tableSuggestions)
        }

        // 7. SQL keywords (context-aware)
        const contextKeywords = getContextKeywords(textBefore)
        const keywordSuggestions = contextKeywords
            .filter(kw => kw.toLowerCase().startsWith(token))
            .map(kw => ({value: kw, description: 'keyword'}))
        results.push(...keywordSuggestions)

        setSuggestions(withRange(filterByToken(results, token), cursorPosition, token.length))
    }, [schema, checkIndex])

    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && onRun) {
            e.preventDefault()
            onRun()
        }
    }, [onRun])

    const textareaStyle = useMemo(() => ({
        minHeight,
        maxHeight,
    }), [minHeight, maxHeight])

    return (
        <div className="border rounded-md" data-testid="sql-editor">
            <CompletionEditor
                value={sql}
                onChange={onChange}
                suggestions={suggestions}
                onSuggestionsRequested={handleSuggestionsRequested}
                className={`border-0 rounded-none resize-y`}
                style={textareaStyle}
                rows={6}
                readOnly={readOnly}
                textareaTestId="sql-editor-textarea"
                onKeyDown={handleKeyDown}
            />
        </div>
    )
}

function filterByToken(suggestions: Suggestion[], token: string): Suggestion[] {
    if (!token) return suggestions
    return suggestions.filter(s => s.value.toLowerCase().startsWith(token))
}

/* WHY: CompletionEditor.applySuggestion requires every suggestion to carry an
   explicit replace range (start/end/text). The SqlEditor builds suggestions
   from local helpers that only know value+description, so accepting a
   suggestion would throw "Suggestion is missing replace range". Stamp the
   range here from the cursor position and the trailing token length. */
function withRange(suggestions: Suggestion[], cursorPosition: number, tokenLength: number): Suggestion[] {
    const start = cursorPosition - tokenLength
    const end = cursorPosition
    return suggestions.map(s => ({
        ...s,
        replaceStart: start,
        replaceEnd: end,
        replaceText: s.value,
    }))
}
